/**
 * Result Card Component
 *
 * Displays a single evaluation result with feedback form:
 * - Question
 * - Response (rendered as markdown)
 * - Reasoning (collapsible box, rendered as markdown)
 * - References (collapsible box)
 * - LLM judge score (hidden from user, stored in data attribute)
 * - Feedback form: thumbs up/down + notes
 */

import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { EvaluationResult } from '../../api/evaluation';
import { useEvaluationStore } from '../../stores/evaluationStore';

interface ResultCardProps {
  result: EvaluationResult;
  index: number;
  readOnly?: boolean;
}

export const ResultCard: React.FC<ResultCardProps> = ({ result, index, readOnly = false }) => {
  const { submitFeedback } = useEvaluationStore();

  // Local state for feedback form
  const [thumbsUp, setThumbsUp] = useState<boolean | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Local state for collapsible sections
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [llmJudgeOpen, setLlmJudgeOpen] = useState(false);

  const handleSubmit = async () => {
    if (thumbsUp === null || !result.trace_id) return;

    setSubmitting(true);
    try {
      await submitFeedback(result.trace_id, thumbsUp, notes);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow">
      <div className="space-y-4">
        {/* Header with Index */}
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="text-lg font-semibold text-gray-900">Result #{index}</h3>
          {submitted && (
            <span className="text-sm text-green-600 font-medium">
              <i className="pi pi-check-circle mr-1" />
              Feedback Submitted
            </span>
          )}
        </div>

        {/* Question */}
        <div>
          <h4 className="text-sm font-semibold text-gray-600 mb-1">Question</h4>
          <p className="text-gray-900">{result.question}</p>
        </div>

        {/* Response - Rendered as Markdown */}
        <div>
          <h4 className="text-sm font-semibold text-gray-600 mb-1">Response</h4>
          <div className="bg-gray-50 rounded-lg p-4">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => (
                  <p style={{
                    fontSize: '14px',
                    margin: '0 0 0.5rem 0',
                    lineHeight: '1.5',
                    color: 'var(--text-primary)'
                  }}>
                    {children}
                  </p>
                ),
                code: ({ children }) => (
                  <code style={{
                    backgroundColor: 'var(--border-muted)',
                    padding: '0.125rem 0.25rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.875em',
                    fontFamily: 'monospace'
                  }}>
                    {children}
                  </code>
                ),
                pre: ({ children }) => (
                  <pre style={{
                    backgroundColor: 'var(--border-muted)',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    overflow: 'auto',
                    fontSize: '0.875em',
                    fontFamily: 'monospace',
                    margin: '0.5rem 0'
                  }}>
                    {children}
                  </pre>
                ),
                ul: ({ children }) => (
                  <ul style={{
                    margin: '0.5rem 0',
                    paddingLeft: '1.5rem'
                  }}>
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol style={{
                    margin: '0.5rem 0',
                    paddingLeft: '1.5rem'
                  }}>
                    {children}
                  </ol>
                ),
                strong: ({ children }) => (
                  <strong style={{ fontWeight: '700' }}>
                    {children}
                  </strong>
                ),
                em: ({ children }) => (
                  <em style={{ fontStyle: 'italic' }}>
                    {children}
                  </em>
                ),
                table: ({ children }) => (
                  <div style={{ overflowX: 'auto', margin: '0.5rem 0' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead style={{ backgroundColor: 'var(--surface-variant)' }}>
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody>
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr style={{ borderBottom: '1px solid var(--border-muted)' }}>
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th style={{
                    textAlign: 'left',
                    padding: '0.5rem',
                    borderBottom: '2px solid var(--border-muted)'
                  }}>
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td style={{
                    padding: '0.5rem',
                    borderBottom: '1px solid var(--border-muted)'
                  }}>
                    {children}
                  </td>
                )
              }}
            >
              {result.response}
            </ReactMarkdown>
          </div>
        </div>

        {/* Reasoning - Collapsible Box with Markdown */}
        {result.reasoning && (
          <div>
            <button
              onClick={() => setReasoningOpen(!reasoningOpen)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
            >
              <i className={`pi ${reasoningOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
              Reasoning
            </button>
            {reasoningOpen && (
              <div className="mt-2 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    p: ({ children }) => (
                      <p style={{
                        fontSize: '14px',
                        margin: '0 0 0.5rem 0',
                        lineHeight: '1.5',
                        color: 'var(--text-primary)'
                      }}>
                        {children}
                      </p>
                    ),
                    code: ({ children }) => (
                      <code style={{
                        backgroundColor: 'var(--color-blue-100)',
                        padding: '0.125rem 0.25rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.875em',
                        fontFamily: 'monospace'
                      }}>
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre style={{
                        backgroundColor: 'var(--color-blue-100)',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        overflow: 'auto',
                        fontSize: '0.875em',
                        fontFamily: 'monospace',
                        margin: '0.5rem 0'
                      }}>
                        {children}
                      </pre>
                    ),
                    ul: ({ children }) => (
                      <ul style={{
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem'
                      }}>
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol style={{
                        margin: '0.5rem 0',
                        paddingLeft: '1.5rem'
                      }}>
                        {children}
                      </ol>
                    )
                  }}
                >
                  {result.reasoning}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )}

        {/* References - Collapsible Box */}
        {result.references && result.references.length > 0 && (
          <div>
            <button
              onClick={() => setReferencesOpen(!referencesOpen)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
            >
              <i className={`pi ${referencesOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
              References ({result.references.length})
            </button>
            {referencesOpen && (
              <div className="mt-2 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                <ul className="space-y-2">
                  {result.references.map((ref, idx) => {
                    // Format: "title P. page_number file id - file_id"
                    const title = ref.title || ref.source || 'Untitled';
                    const pageNumber = ref.page_number || ref.page;
                    const fileId = ref.file_id;

                    let displayText = title;
                    if (pageNumber) {
                      displayText += ` P. ${pageNumber}`;
                    }
                    if (fileId) {
                      displayText += ` file id - ${fileId}`;
                    }

                    return (
                      <li key={idx} className="flex items-start gap-2">
                        <i className="pi pi-book text-purple-600 mt-1" style={{ fontSize: '0.875rem' }} />
                        <span className="text-sm text-gray-800">
                          {displayText}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* LLM-as-Judge Score - Collapsible Box (only show in readOnly mode or after submission) */}
        {(result.llm_judge_score !== null && result.llm_judge_score !== undefined) && (readOnly || submitted) && (
          <div>
            <button
              onClick={() => setLlmJudgeOpen(!llmJudgeOpen)}
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
            >
              <i className={`pi ${llmJudgeOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
              LLM-as-Judge Evaluation
              {result.llm_judge_score !== null && result.llm_judge_score !== undefined && (
                <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                  result.llm_judge_score >= 0.8 ? 'bg-green-100 text-green-800' :
                  result.llm_judge_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  Score: {(result.llm_judge_score * 100).toFixed(0)}%
                </span>
              )}
            </button>
            {llmJudgeOpen && (
              <div className="mt-2 p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
                <div className="space-y-3">
                  {/* Score */}
                  <div>
                    <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      Score
                    </h5>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            result.llm_judge_score >= 0.8 ? 'bg-green-600' :
                            result.llm_judge_score >= 0.6 ? 'bg-yellow-600' :
                            'bg-red-600'
                          }`}
                          style={{ width: `${(result.llm_judge_score || 0) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-900">
                        {((result.llm_judge_score || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>

                  {/* Comment */}
                  {result.llm_judge_comment && (
                    <div>
                      <h5 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                        Evaluation Reasoning
                      </h5>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => (
                            <p style={{
                              fontSize: '14px',
                              margin: '0 0 0.5rem 0',
                              lineHeight: '1.5',
                              color: 'var(--text-primary)'
                            }}>
                              {children}
                            </p>
                          ),
                          code: ({ children }) => (
                            <code style={{
                              backgroundColor: 'var(--color-green-100)',
                              padding: '0.125rem 0.25rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.875em',
                              fontFamily: 'monospace'
                            }}>
                              {children}
                            </code>
                          ),
                          ul: ({ children }) => (
                            <ul style={{
                              margin: '0.5rem 0',
                              paddingLeft: '1.5rem'
                            }}>
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol style={{
                              margin: '0.5rem 0',
                              paddingLeft: '1.5rem'
                            }}>
                              {children}
                            </ol>
                          )
                        }}
                      >
                        {result.llm_judge_comment}
                      </ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Feedback Section */}
        <div className="border-t pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            {readOnly ? 'Submitted Feedback' : 'Your Feedback'}
          </h4>

          {readOnly ? (
            /* Show existing feedback in read-only mode */
            result.human_feedback_score !== null && result.human_feedback_score !== undefined ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <i className={`pi ${
                    result.human_feedback_score === 1.0 ? 'pi-thumbs-up text-green-600' : 'pi-thumbs-down text-red-600'
                  } text-xl`} />
                  <span className="font-medium text-gray-900">
                    {result.human_feedback_score === 1.0 ? 'Thumbs Up' : 'Thumbs Down'}
                  </span>
                </div>
                {result.human_feedback_notes && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Notes:</p>
                    <p className="text-sm text-gray-800 mt-1">{result.human_feedback_notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <i className="pi pi-info-circle mr-2" />
                  No feedback was submitted for this result.
                </p>
              </div>
            )
          ) : !submitted ? (
            <>
              {/* Thumbs Up/Down Buttons */}
              <div className="flex items-center gap-3 mb-4">
                <Button
                  icon="pi pi-thumbs-up"
                  className={`p-button-lg ${
                    thumbsUp === true ? 'p-button-success' : 'p-button-outlined'
                  }`}
                  onClick={() => setThumbsUp(true)}
                  disabled={submitting}
                  aria-label="Thumbs up"
                />
                <Button
                  icon="pi pi-thumbs-down"
                  className={`p-button-lg ${
                    thumbsUp === false ? 'p-button-danger' : 'p-button-outlined'
                  }`}
                  onClick={() => setThumbsUp(false)}
                  disabled={submitting}
                  aria-label="Thumbs down"
                />
                {thumbsUp !== null && (
                  <span className="text-sm text-gray-600 ml-2">
                    {thumbsUp ? 'Good answer' : 'Needs improvement'}
                  </span>
                )}
              </div>

              {/* Notes Textarea */}
              <div className="mb-4">
                <label
                  htmlFor={`notes-${result.item_id}`}
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Notes (Optional)
                </label>
                <InputTextarea
                  id={`notes-${result.item_id}`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional feedback or comments..."
                  rows={3}
                  className="w-full"
                  disabled={submitting}
                />
              </div>

              {/* Submit Button */}
              <Button
                label={submitting ? 'Submitting...' : 'Submit Feedback'}
                icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
                onClick={handleSubmit}
                disabled={thumbsUp === null || submitting || !result.trace_id}
                className="p-button-primary"
                size="small"
              />

              {!result.trace_id && (
                <p className="mt-2 text-xs text-red-600">
                  <i className="pi pi-exclamation-triangle mr-1" />
                  No trace ID available. Cannot submit feedback.
                </p>
              )}
            </>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <i className="pi pi-check-circle mr-2" />
                Thank you for your feedback!
                {notes && (
                  <span className="block mt-2 text-gray-700">Your notes: "{notes}"</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
